import {
  useBenefitsEnrolmentsMinMaxFormulaFieldRecalculateCreate,
  useModularBenefitsMinMaxFormulaFieldRecalculateCreate,
} from '@ben/api/gen/codegen';
import { IS_EA, IS_ECC } from '@ben/shared/constants/is-app';
import { useDebouncedCallback } from '@mantine/hooks';
import { isEqual, isNil } from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

const RECALCULATE_DEBOUNCE_MS = 300;

interface FormulaFieldResult {
  min: number;
  max: number;
  /**
   * Recalculated sell capacity. Only returned by the API for
   * BUY_SELL_HOLIDAY fields; undefined until the first recalculation
   * response (callers fall back to the initially serialized sell_max).
   */
  sellMax?: number;
  isLoading: boolean;
}

interface UseFormulaFieldRecalculationProps {
  benefitForm?: Record<string, unknown>;
  min: number;
  max: number;
  componentKey: string;
  hasCalculatedFormula: boolean;
  benefitId?: number;
  enrolmentId?: number;
  onChange?: (value: unknown) => void;
  disabled?: boolean;
  clampValueOnBoundsChange?: boolean;
}

interface RecalculateResult {
  minValue?: string;
  maxValue?: string;
  sellMax?: string;
}

interface LastKnownBounds {
  min?: number;
  max?: number;
  sellMax?: number;
}

const parseBounds = (
  data: RecalculateResult,
  fallback: LastKnownBounds
): LastKnownBounds => ({
  min: isNil(data.minValue) ? fallback.min : Number.parseFloat(data.minValue),
  max: isNil(data.maxValue) ? fallback.max : Number.parseFloat(data.maxValue),
  sellMax: isNil(data.sellMax)
    ? fallback.sellMax
    : Number.parseFloat(data.sellMax),
});

const getClampedValue = (
  benefitForm: Record<string, unknown> | undefined,
  componentKey: string,
  bounds: LastKnownBounds
): number | undefined => {
  if (!benefitForm) return undefined;
  const currentValue = Number(benefitForm[componentKey]);
  if (!isNil(bounds.max) && currentValue > bounds.max) return bounds.max;
  if (!isNil(bounds.min) && currentValue < bounds.min) return bounds.min;
  return undefined;
};

export function useFormulaMinMaxField({
  benefitForm,
  min,
  max,
  componentKey,
  hasCalculatedFormula,
  benefitId,
  enrolmentId,
  onChange,
  disabled,
  clampValueOnBoundsChange = true,
}: UseFormulaFieldRecalculationProps): FormulaFieldResult {
  const { t } = useTranslation();
  const [previousFormData, setPreviousFormData] = useState<unknown>();
  const [lastKnownBounds, setLastKnownBounds] = useState<LastKnownBounds>({});
  const isCockpit = IS_EA && benefitId !== undefined;
  const isHouston = IS_ECC && enrolmentId !== undefined;

  const { mutate: cockpitMutation, isLoading: isCockpitLoading } =
    useModularBenefitsMinMaxFormulaFieldRecalculateCreate({
      mutation: { onError: () => toast.error(t('errors.minMaxError')) },
    });

  const { mutate: houstonMutation, isLoading: isHoustonLoading } =
    useBenefitsEnrolmentsMinMaxFormulaFieldRecalculateCreate({
      mutation: { onError: () => toast.error(t('errors.minMaxError')) },
    });

  const isRecalculationRequired =
    hasCalculatedFormula &&
    (isCockpit || isHouston) &&
    !!benefitForm &&
    !isEqual(benefitForm, previousFormData);

  const debouncedRecalculate = useDebouncedCallback(() => {
    if (!isRecalculationRequired) return;

    const handleSuccess = (data: RecalculateResult) => {
      const newBounds = parseBounds(data, lastKnownBounds);
      setLastKnownBounds((previous) => parseBounds(data, previous));

      const clampedValue =
        disabled || !clampValueOnBoundsChange
          ? undefined
          : getClampedValue(benefitForm, componentKey, newBounds);
      setPreviousFormData(
        clampedValue === undefined
          ? benefitForm
          : {
              ...benefitForm,
              [componentKey]: clampedValue,
            }
      );

      if (clampedValue !== undefined) {
        onChange?.(clampedValue);
      }
    };

    if (isCockpit) {
      cockpitMutation(
        { benefitPk: benefitId, fieldKey: componentKey, data: benefitForm },
        { onSuccess: handleSuccess }
      );
    } else if (isHouston) {
      houstonMutation(
        { enrolmentPk: enrolmentId, fieldKey: componentKey, data: benefitForm },
        { onSuccess: handleSuccess }
      );
    }
  }, RECALCULATE_DEBOUNCE_MS);

  useEffect(() => {
    debouncedRecalculate();
  }, [debouncedRecalculate, benefitForm]);

  return {
    min: lastKnownBounds.min ?? min,
    max: lastKnownBounds.max ?? max,
    sellMax: lastKnownBounds.sellMax,
    isLoading: isCockpitLoading || isHoustonLoading || isRecalculationRequired,
  };
}

export default useFormulaMinMaxField;
